/**
 * Fetch-streaming SSE parser.
 *
 * EventSource is GET-only and can't carry a multipart body, so the recording
 * upload uses fetch() + ReadableStream and this parser to consume Kyoto's
 * server-sent events response.
 */

/**
 * Parse a single SSE event block (no trailing blank line).
 *
 * @param {string} raw - The event block: one or more `field: value` lines.
 * @returns {{event: string, data: object} | null}
 *   - `{ event, data }` when a `data:` line is present and parses as JSON.
 *   - `null` when there is no `data:` line, or its JSON is invalid.
 */
export function parseSseEvent(raw) {
  const lines = raw.split("\n");
  let event = "message";
  let dataLine = null;
  for (const line of lines) {
    if (line.startsWith("event:")) {
      event = line.slice("event:".length).trim();
    } else if (line.startsWith("data:")) {
      dataLine = line.slice("data:".length).trim();
    }
  }
  if (dataLine === null) return null;
  try {
    return { event, data: JSON.parse(dataLine) };
  } catch {
    return null;
  }
}

/**
 * Consume an SSE byte stream, dispatching parsed events to handlers by name.
 *
 * @param {ReadableStreamDefaultReader} reader - Pre-acquired reader. Caller
 *   is expected to have called `stream.getReader()` themselves.
 * @param {Object<string, (data: object) => void>} handlers - Map from event
 *   name to handler. Events with no matching handler are dropped silently.
 *   Malformed event blocks are skipped silently.
 */
export async function consumeSseStream(reader, handlers) {
  const decoder = new TextDecoder("utf-8");
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let boundary;
    while ((boundary = buf.indexOf("\n\n")) !== -1) {
      const block = buf.slice(0, boundary);
      buf = buf.slice(boundary + 2);
      const parsed = parseSseEvent(block);
      if (!parsed) continue;
      const handler = handlers[parsed.event];
      if (handler) handler(parsed.data);
    }
  }
}
